import { useRuleCap } from '../rules/RuleFlow'
import { Button } from '../../platform/primitives/Button'
import { BULK_CAP, useSelection } from './selection'
import './transactions.css'

export type BulkToolbarAction = 'set_category' | 'set_merchant' | 'add_tag' | 'assign_owner' | 'create_rule' | 'delete'

const ACTIONS: { action: BulkToolbarAction; label: string }[] = [
  { action: 'set_category', label: 'Set category' },
  { action: 'set_merchant', label: 'Set merchant' },
  { action: 'add_tag', label: 'Add tag' },
  { action: 'assign_owner', label: 'Assign owner' },
  { action: 'create_rule', label: 'Create rule from these' },
  { action: 'delete', label: 'Delete' },
]

const REASON_ID = 'txn-bulk-reason'
const RULE_CAP_ID = 'txn-bulk-rule-cap'

/**
 * The bulk bar (M6-R6). Delete (#799) is the one destructive action: it opens a confirm, never deletes on press.
 *
 * The count is a polite live region and is mounted before anything is
 * selected — a region inserted already holding its text is not announced, so
 * the first selection would be silent. While nothing is selected the bar is
 * visually hidden and holds only the empty region.
 *
 * Over the cap every action is `aria-disabled`, not `disabled`: a natively
 * disabled button leaves the tab order, so a keyboard user would never reach
 * the sentence that says why. The reason is real adjacent text and the buttons
 * point at it. Clear selection stays enabled — it is the way out of being over.
 *
 * `docked` is the phone form: a glass bar along the bottom edge. It floats over
 * content, which is the one place glass is legal (rule 9).
 */
export function BulkToolbar({ docked, onAction }: { docked?: boolean; onAction: (a: BulkToolbarAction) => void }) {
  const { count, dispatch } = useSelection()
  const over = count > BULK_CAP
  const rules = useRuleCap()
  const active = count > 0
  return (
    // One element, mounted throughout: the live region inside it must exist
    // before it has text, so the bar's box is never swapped for another.
    <div
      role={active ? 'toolbar' : undefined}
      aria-label={active ? 'Bulk actions' : undefined}
      className={active ? `txn-bulk${docked ? ' txn-bulk--docked' : ''}` : 'sr-only'}
    >
      <span role="status" aria-live="polite" className="txn-bulk__count">
        {active ? `${count} selected` : ''}
      </span>
      {active && (
        <div className="txn-bulk__actions">
          {ACTIONS.map(({ action, label }) => {
            // "Create rule" is also off at the rules cap, with its own reason.
            const capped = action === 'create_rule' && rules.atCap
            const off = over || capped
            return (
              <Button
                key={action}
                type="button"
                size="page"
                variant={action === 'delete' ? 'destructive' : 'outline'}
                aria-disabled={off || undefined}
                aria-describedby={over ? REASON_ID : capped ? RULE_CAP_ID : undefined}
                onClick={() => {
                  if (!off) onAction(action)
                }}
              >
                {label}
              </Button>
            )
          })}
          <Button type="button" size="page" variant="chip" onClick={() => dispatch({ t: 'clear' })}>
            Clear selection
          </Button>
        </div>
      )}
      {active && rules.atCap && (
        <span id={RULE_CAP_ID} role="note" className="note txn-bulk__reason">
          {`Create rule: ${rules.reason}`}
        </span>
      )}
      {over && (
        <span id={REASON_ID} role="note" className="note txn-bulk__reason">
          {`Select ${BULK_CAP} or fewer. ${count} selected.`}
        </span>
      )}
    </div>
  )
}
